import { Clock, ShieldCheck, Trash2 } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { DynamicText } from '@/components/dynamic-text';

/**
 * Three short, literally-true claims about the feature — same rhetorical shape as the
 * Hero's `CLAIMS` panel, applied to one feature instead of the whole app.
 */
const BABYSITTER_CLAIMS = [
  {
    icon: Clock,
    title: 'Access to one child',
    body: 'Give a babysitter access to a specific child. They see that child and nothing else in the family.',
  },
  {
    icon: ShieldCheck,
    title: 'Full logging capabilities',
    body: 'Babysitters can log feeds, diapers, sleep, and everything else — exactly like a co-parent, just restricted to one child.',
  },
  {
    icon: Trash2,
    title: 'Revoke anytime',
    body: "Their access lasts until you choose to revoke it. When you do, their phone clears its copy of your child's log.",
  },
] as const;

export function BabysitterMode() {
  return (
    <section id="babysitter" className="container scroll-mt-24 py-20 sm:py-28">
      <div className="max-w-lg">
        <p className="kicker">
          <DynamicText path="babysitter.kicker">Babysitter mode</DynamicText>
        </p>
        <h2 className="mt-3 text-[clamp(2rem,4.5vw,3rem)] leading-[1.05]">
          <DynamicText path="babysitter.title">Date nights included</DynamicText>
        </h2>
        <p className="mt-4 text-muted-foreground">
          <DynamicText path="babysitter.description">
            Give your babysitter access to record events for a specific window of time. Everything they enter gets synced to your shared timeline.
          </DynamicText>
        </p>
      </div>

      <div className="mt-12 grid gap-6 sm:grid-cols-3">
        {BABYSITTER_CLAIMS.map(({ icon: Icon, title, body }, idx) => (
          <Card key={title} className="bg-card/60">
            <CardHeader>
              <Icon aria-hidden="true" className="size-6 text-berry" />
              <CardTitle className="mt-3 font-display text-lg">
                <DynamicText path={`babysitter.claims.${idx}.title`}>{title}</DynamicText>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription className="text-[0.95rem] leading-relaxed">
                <DynamicText path={`babysitter.claims.${idx}.body`}>{body}</DynamicText>
              </CardDescription>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}
